/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

interface InvocationInfo {
  startOfBatch: number; // Timestamp, Date.getTime()
  count: number;
}

enum Invocation {
  signIn = "sign-in-info",
  signOut = "sign-out-info",
}

const countInvocations = (
  invocation: Invocation,
  runner: (info: InvocationInfo) => void
): VoidFunction => {
  return (): void => {
    const info = getInvocationInfo(invocation);
    const now = new Date().getTime();

    // 5 minute window
    if (now > info.startOfBatch + 1000 * 60 * 5) {
      const newBatch: InvocationInfo = {
        startOfBatch: new Date().getTime(),
        count: 1,
      };
      setInvocationInfo(invocation, newBatch);
      runner(newBatch);
    } else {
      const updatedBatch: InvocationInfo = {
        ...info,
        count: info.count + 1,
      };
      setInvocationInfo(invocation, updatedBatch);
      runner(updatedBatch);
    }
  };
};

const getInvocationInfo = (invocation: Invocation): InvocationInfo => {
  const info = localStorage.getItem(invocation);
  if (!info) {
    return {
      startOfBatch: new Date().getTime(),
      count: 0,
    };
  }

  return JSON.parse(info);
};

const setInvocationInfo = (
  invocation: Invocation,
  info: InvocationInfo
): void => {
  localStorage.setItem(invocation, JSON.stringify(info));
};

const resetInvocationInfo = (invocation: Invocation): void => {
  localStorage.removeItem(invocation);
};

export const signOutAndIncrement: VoidFunction = countInvocations(
  Invocation.signOut,
  (invocations) => {
    if (invocations.count >= 3) {
      newrelic.noticeError("Sign out loop", {
        count: invocations.count,
      });
    }
    adobeIMS?.signOut();
  }
);

export const signInAndIncrement: VoidFunction = countInvocations(
  Invocation.signIn,
  (invocations) => {
    if (invocations.count >= 3) {
      newrelic.noticeError("Sign in redirect loop");
      resetInvocationInfo(Invocation.signIn);
      signOutAndIncrement();
    } else {
      adobeIMS?.signIn();
    }
  }
);
